"use client";
import React, { useEffect, useState } from "react";
import { useCookies } from "next-client-cookies";
import { CookieIcon } from "../Icons";
import { Modal } from "../modal";
import { useTranslations } from "next-intl";

type Props = {
  openCookieModal?: boolean;
  page?: string;
  callBackFn?: () => void;
};

const cookieObj = { requiredCookies: true, maps: false };

const CookieDialog: React.FC<Props> = ({
  page,
  callBackFn,
  openCookieModal = false,
}) => {
  const [mapCookie, setMapCookie] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const t = useTranslations();

  const cookies = useCookies();

  useEffect(() => {
    if (!page) {
      const isEnabled = cookies.get("cookieSettings");
      if (!isEnabled) {
        setOpenModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCookie = (obj: {}) => {
    cookies.set("cookieSettings", JSON.stringify(obj));
    const cookieValue = JSON.stringify(obj);
    const date = new Date();
    date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);

    cookies.set("cookieSettings", cookieValue, { expires: date });

    if (callBackFn) {
      callBackFn();
    } else {
      setOpenModal(false);
    }
  };

  const acceptAllCookies = () => {
    cookieObj.maps = true;
    setCookie(cookieObj);
  };

  const rejectAllCookies = () => {
    cookieObj.maps = false;
    setCookie(cookieObj);
  };

  const saveSelectedCookies = () => {
    cookieObj.maps = mapCookie;
    setCookie(cookieObj);
  };

  return (
    <Modal
      isOpen={openModal || openCookieModal}
      width="w-full 2xl:w-[30%] lg:w-[40%] sm:w-2/3 !rounded-2xl"
      height="p-6"
      outerClassName="overflow-y-scroll"
      innerClassName="m-0.5 rounded-2xl p-0"
      bgColor="bg-lightwhite dark:bg-sleap-gray-950"
    >
      <div className="flex flex-col gap-8 rounded-2xl bg-lightwhite dark:bg-sleap-gray-950 dark:text-lightwhite text-sleap-gray-950">
        <div className="flex flex-col sm:gap-6 gap-4">
          <div className="flex items-center gap-4">
            {/* <div className="w-fit bg-gradient-to-r from-primary to-secondary p-4"> */}
            <div id="cookie-icon" className="h-8 w-8">
              <CookieIcon />
            </div>
            {/* </div> */}
            <h3 className="text-2xl mb-0">{t("Other.cookies")}</h3>
          </div>
          <div>
            <p className="mb-0 sm:text-base text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
              {t("Other.weOnlyUseDataWithYourConsent")}
            </p>
          </div>
          <div className="flex flex-col sm:gap-6 gap-4">
            <div className="flex-col">
              <div className="flex gap-2">
                <input
                  className="w-4 h-4 mt-1 accent-primary rounded-sm border-2 cursor-pointer"
                  type="checkbox"
                  id="required-cookies"
                  checked={true}
                />

                <label
                  id="required-cookies"
                  className="font-semibold text-base"
                >
                  {t("Other.required")}
                </label>
              </div>
              <p className="mb-0 text-sm font-normal ms-6 dark:text-sleap-gray-100 text-sleap-gray-800">
                {t("Other.theseCookiesAreNecessary")}
              </p>
            </div>
            <div className="flex-col">
              <div className="flex gap-2">
                <input
                  className="w-4 h-4 mt-1 accent-primary rounded-sm border-2 cursor-pointer"
                  type="checkbox"
                  id="maps-cookies"
                  checked={mapCookie}
                  onChange={() => setMapCookie(!mapCookie)}
                />
                <label
                  htmlFor="maps-cookies"
                  className="font-semibold text-base"
                >
                  {t("Other.maps")}
                </label>
              </div>
              <p className="mb-0 text-sm font-normal ms-6 dark:text-sleap-gray-100 text-sleap-gray-800">
                {t("Other.theseCookiesAreNecessaryForMap")}
              </p>
            </div>
            <p className="mb-0 sm:text-base text-sm font-normal dark:text-sleap-gray-100 text-sleap-gray-800">
              {t("Other.forMoreInformationSee")}{" "}
              {page !== "privacy" && (
                <a href="/privacy-policy" target="_blank">
                  {t("Other.privacyPolicy")}
                </a>
              )}
            </p>
          </div>
        </div>

        <div className="sm:block flex sm:flex-row flex-col gap-2 justify-between gap-y-4">
          <button
            type="button"
            className="primary-border md:px-4 px-3 py-2 rounded-xl w-full sm:hidden gap-x-2 dark:text-lightwhite text-sleap-gray-950"
            onClick={acceptAllCookies}
          >
            <h5 className="text-sm font-medium mb-0 font-inter">
              {t("Other.acceptAll")}
            </h5>
          </button>
          <div className="flex gap-2 justify-between">
            <button
              type="button"
              className="border border-sleap-gray-400 md:px-4 px-3 py-2 rounded-xl flex gap-x-2 dark:text-lightwhite text-sleap-gray-950"
              onClick={rejectAllCookies}
            >
              <h5 className="text-sm font-medium mb-0 font-inter">
                {t("Other.rejectAll")}
              </h5>
            </button>
            <div className="flex gap-x-2">
              <button
                type="button"
                className="border dark:border-sleap-gray-600 border-sleap-gray-100 md:px-4 px-3 py-2 rounded-xl flex gap-x-2 dark:text-lightwhite text-sleap-gray-950 bg-sleap-gray-200 dark:bg-sleap-gray-700"
                onClick={saveSelectedCookies}
              >
                <h5 className="text-sm font-medium mb-0 font-inter">
                  {t("Other.saveSelection")}
                </h5>
              </button>
              <button
                type="button"
                className="primary-border md:px-4 px-3 py-2 rounded-xl sm:flex hidden gap-x-2 text-lightwhite "
                onClick={acceptAllCookies}
              >
                <h5 className="text-sm font-medium mb-0 font-inter">
                  {t("Other.acceptAll")}
                </h5>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CookieDialog;
